import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTriggerType,
  BlankContent,
  HeadingText,
  MenuEmpty,
  MenuInput,
  MenuItems,
  NextAlert,
  NextSeparator,
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuProvider,
  SelectMenuTriggerType,
  Spinner,
  StatData,
} from '@cint/wonderment';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { useReportContext } from '@/context/ReportContext';
import { formatCurrencyValue } from '@/utils/formatCurrencyValue';
import { formatUTCDateForLastUpdated } from '@/utils/reports/formatUTCDateForLastUpdated';
import { CompaniesResponse, DropDownItem } from '@/utils/types';
import { URLS } from '@/utils/urls';

import { PanelistInsightsReportFilters } from './PanelistInsightsReportFilters';
import { RecruitmentReportFilters } from './RecruitmentReportFilters';

interface ReportHeaderProps {
  title: string;
  reportName:
    | 'financial'
    | 'marketing'
    | 'survey'
    | 'retention'
    | 'demographics'
    | 'overview';
  onPanelCreatedDateChange?: (date: Date | null) => void;
}

export const ReportHeader = ({
  reportName,
  onPanelCreatedDateChange,
}: ReportHeaderProps) => {
  const {
    selectedCompany,
    setSelectedCompany,
    selectedPanel,
    setSelectedPanel,
    filters,
    setFilters,
    lastUpdatedAt,
  } = useReportContext();

  const queryClient = useQueryClient();

  const {
    isLoading: isPanelsLoading,
    data: panelsData,
    error: panelsError,
  } = useFetchData<DropDownItem[]>({
    url: URLS.getAllCompanyPanels(selectedCompany),
    enabled: !!selectedCompany,
  });

  const {
    isLoading: isCompaniesLoading,
    data: companiesData,
    error: companiesError,
  } = useFetchData<CompaniesResponse>({
    url: URLS.getCompanies(),
    staleTime: 10 * 60 * 60 * 1000,
  });

  const metricsDataQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (reportName === 'marketing') {
      params.append('panel_ids', selectedPanel);
    } else {
      const { selectedPanelsForComparison } = filters;
      selectedPanelsForComparison?.length &&
        selectedPanelsForComparison.forEach((panelId) =>
          params.append('panel_ids', panelId)
        );
    }
    return params.toString();
  }, [filters, reportName, selectedPanel]);

  //todo update the response type when available
  const {
    isLoading: isMetricsLoading,
    data: metricsData,
    error: metricsError,
  } = useFetchData<any>({
    url: URLS.getPanelMetrics(selectedCompany, metricsDataQueryString),
    enabled: !!selectedCompany && !!selectedPanel,
  });

  const showError = companiesError || metricsError || panelsError;

  const companiesListOptions = useMemo(
    () =>
      companiesData?.data.map(({ name, id }) => ({ label: name, value: id })),
    [companiesData]
  );

  const selectedCompanyName = useMemo(() => {
    return companiesListOptions?.find(({ value }) => value === selectedCompany)
      ?.label;
  }, [companiesListOptions, selectedCompany]);

  // set first company in the list as selected by default
  useEffect(() => {
    if (!selectedCompany) {
      setSelectedCompany(companiesListOptions?.[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesListOptions]);

  const handleSelectCompany = (value) => {
    const [, companyId] = JSON.parse(value);
    queryClient.removeQueries({
      queryKey: [URLS.getAllCompanyPanels(selectedCompany)],
    });
    setSelectedPanel(null);
    setSelectedCompany(companyId);
  };

  const handleSelectPanel = (value) => {
    setSelectedPanel(value);
  };

  const panelsListOptions = useMemo(() => {
    if (panelsData?.length) {
      return panelsData.map(({ value, key }) => ({
        label: value,
        value: key,
      }));
    }
    return [];
  }, [panelsData]);

  const selectedPanelName = useMemo(() => {
    return (
      panelsListOptions?.find(({ value }) => value === selectedPanel)?.label ||
      null
    );
  }, [panelsListOptions, selectedPanel]);

  // set first panel in the list as selected by default
  useEffect(() => {
    if (selectedPanel && panelsData?.length === 0) {
      setSelectedPanel(null);
      setFilters({
        selectedPanelsForComparison: null,
      });
    } else if (!selectedPanel && panelsData?.length) {
      setSelectedPanel(panelsData[0].key);
    } else if (selectedPanel && !selectedPanelName && panelsData?.length) {
      setSelectedPanel(panelsData[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelsData, selectedPanel, selectedPanelName]);

  const formattedLastUpdatedAt =
    lastUpdatedAt && formatUTCDateForLastUpdated(lastUpdatedAt);

  const countriesDisplayValue =
    `${metricsData?.countries?.join(', ').slice(0, 20)}${
      metricsData?.countries?.join(', ').length > 20 ? '...' : ''
    }` || '-';

  const languagesDisplayValue =
    `${metricsData?.languages?.join(', ').slice(0, 20)}${
      metricsData?.languages?.join(', ').length > 20 ? '...' : ''
    }` || '-';

  return (
    <>
      <div className="container px-8">
        <div className="mb-8">
          {lastUpdatedAt && (
            <div className="pb-5">
              <NextAlert
                iconName="Info"
                isDismissible
                description={`Information was last updated on ${formattedLastUpdatedAt} UTC`}
                variant="info"
              />
            </div>
          )}
          {isCompaniesLoading && <Spinner />}
          <div>
            {!(companiesListOptions?.length > 1) && (
              <HeadingText fontWeight="normal" variant="h3">
                {selectedCompanyName}
              </HeadingText>
            )}
            {selectedCompany && companiesListOptions?.length > 1 && (
              <SelectMenuProvider
                isMultiSelect={false}
                onSelectedChange={handleSelectCompany}
                selectedItems={selectedCompany ? [selectedCompany] : []}
              >
                <SelectMenu
                  triggerProps={{
                    type: SelectMenuTriggerType.InlineSelect,
                    props: {
                      text: selectedCompanyName || 'Select company',
                      fontWeight: 'normal',
                      size: 'h3',
                    },
                  }}
                >
                  <SelectMenuContent>
                    <MenuInput placeholder="Search" />
                    {!companiesListOptions?.length && (
                      <MenuEmpty>No results.</MenuEmpty>
                    )}
                    <MenuItems>
                      {companiesListOptions?.map(({ label, value }) => (
                        <SelectMenuItem
                          key={value}
                          label={label}
                          // SelectMenuItem accepts only string as a value. We are using JSON.stringify to ensure the value contains both label to be searched and value to be used in the onSelectedChange callback.
                          value={JSON.stringify([label, value])}
                        />
                      ))}
                    </MenuItems>
                  </SelectMenuContent>
                </SelectMenu>
              </SelectMenuProvider>
            )}
            <div className="flex gap-2 items-baseline mt-8 mb-6">
              <div>
                {isPanelsLoading && <Spinner />}
                {!!panelsListOptions?.length &&
                  selectedPanel &&
                  !isPanelsLoading && (
                    <ActionMenu
                      triggerProps={{
                        type: ActionMenuTriggerType.SelectButton,
                        props: {
                          label: selectedPanelName,
                        },
                      }}
                    >
                      <ActionMenuContent>
                        <MenuInput placeholder="Search" />
                        <MenuEmpty>No results.</MenuEmpty>
                        <MenuItems>
                          {panelsListOptions.map(({ label, value }) => (
                            <ActionMenuItem
                              key={value}
                              label={label}
                              onClick={() => handleSelectPanel(value)}
                            />
                          ))}
                        </MenuItems>
                      </ActionMenuContent>
                    </ActionMenu>
                  )}
              </div>
              {selectedPanel && reportName === 'marketing' && (
                <RecruitmentReportFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  selectedPanel={selectedPanel}
                  selectedCompany={selectedCompany}
                />
              )}
              {selectedPanel && reportName !== 'marketing' && (
                <PanelistInsightsReportFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  selectedPanel={selectedPanel}
                  selectedCompany={selectedCompany}
                  onPanelCreatedDateChange={onPanelCreatedDateChange}
                />
              )}
            </div>
            <div
              role="list"
              className="flex justify-between h-18 min-h-18 w-full gap-10"
            >
              {isMetricsLoading && <Spinner />}
              {metricsData && (
                <>
                  <div className="flex gap-10 grow">
                    <StatData
                      alignment="left"
                      labelProps={{
                        label: 'AU 7 days',
                      }}
                      size="3xl"
                      value={
                        metricsData.active_panelists_last7_days
                          ? new Intl.NumberFormat().format(
                              metricsData.active_panelists_last7_days
                            )
                          : '-'
                      }
                    />
                    <StatData
                      alignment="left"
                      labelProps={{
                        label: 'AU 30 days',
                      }}
                      role="listitem"
                      size="3xl"
                      value={
                        metricsData.active_panelists_last30_days
                          ? new Intl.NumberFormat().format(
                              metricsData.active_panelists_last30_days
                            )
                          : '-'
                      }
                    />
                    <StatData
                      alignment="left"
                      labelProps={{
                        label: 'AU 90 days',
                      }}
                      role="listitem"
                      size="3xl"
                      value={
                        metricsData.active_panelists_last90_days
                          ? new Intl.NumberFormat().format(
                              metricsData.active_panelists_last90_days
                            )
                          : '-'
                      }
                    />
                    <StatData
                      alignment="left"
                      labelProps={{
                        label: 'AU 365 days',
                      }}
                      role="listitem"
                      size="3xl"
                      value={
                        metricsData.active_panelists_last365_days
                          ? new Intl.NumberFormat().format(
                              metricsData.active_panelists_last365_days
                            )
                          : '-'
                      }
                    />
                  </div>
                  <NextSeparator variant="content" direction="vertical" />
                  <div className="min-w-xs">
                    <StatData
                      alignment="tabular"
                      labelProps={{
                        label: 'Country',
                        tooltipContent:
                          metricsData.countries?.join(', ') || '-',
                      }}
                      labelPosition="inline"
                      size="md"
                      value={
                        metricsData.countries ? countriesDisplayValue : '-'
                      }
                    />
                    <StatData
                      alignment="tabular"
                      labelProps={{
                        label: 'Language',
                        tooltipContent:
                          metricsData.languages?.join(', ') || '-',
                      }}
                      labelPosition="inline"
                      size="md"
                      value={
                        metricsData.languages ? languagesDisplayValue : '-'
                      }
                    />
                    <StatData
                      alignment="tabular"
                      labelPosition="inline"
                      labelProps={{
                        label: 'YTD total panelists',
                        tooltipContent:
                          'Number of panelists in the panel from the start of the year to date.',
                      }}
                      size="md"
                      value={
                        metricsData.year_to_date_total_panelists
                          ? new Intl.NumberFormat().format(
                              metricsData.year_to_date_total_panelists
                            )
                          : '-'
                      }
                    />
                  </div>
                  <NextSeparator variant="content" direction="vertical" />
                  <div className="min-w-xs">
                    <StatData
                      alignment="tabular"
                      labelProps={{
                        label: 'YTD completes',
                        tooltipContent:
                          'Number of survey completes in the panel from the start of the year to date.',
                      }}
                      labelPosition="inline"
                      size="md"
                      value={
                        metricsData.year_to_date_completes
                          ? new Intl.NumberFormat().format(
                              metricsData.year_to_date_completes
                            )
                          : '-'
                      }
                    />
                    <StatData
                      alignment="tabular"
                      labelProps={{
                        label: 'YTD revenue',
                        tooltipContent:
                          'Total revenue generated from panelists activities from the start of the year to date. The revenue in this chart includes only revenue coming from booked participations.',
                      }}
                      labelPosition="inline"
                      size="md"
                      value={
                        metricsData.year_to_date_revenues?.value
                          ? (formatCurrencyValue(
                              metricsData.year_to_date_revenues?.value,
                              metricsData.year_to_date_revenues?.currency_scale,
                              metricsData.year_to_date_revenues?.currency_code
                            ) as string)
                          : '-'
                      }
                    />
                    <StatData
                      alignment="tabular"
                      labelProps={{
                        label: 'YTD cost',
                        tooltipContent:
                          'Total incentives paid to panelists from the start of the year to date. The costs in this chart include only costs coming from booked participations.',
                      }}
                      labelPosition="inline"
                      size="md"
                      value={
                        metricsData.year_to_date_costs?.value
                          ? (formatCurrencyValue(
                              metricsData.year_to_date_costs?.value,
                              metricsData.year_to_date_costs?.currency_scale,
                              metricsData.year_to_date_costs?.currency_code
                            ) as string)
                          : '-'
                      }
                    />
                  </div>
                </>
              )}
            </div>
            {showError && (
              <BlankContent
                description="Please try again later"
                size="sm"
                title="Something went wrong"
              />
            )}
            <NextSeparator className="mt-8" variant="page" />
          </div>
        </div>
      </div>
    </>
  );
};
